import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import useViewport from 'Hooks/useViewport';
import pauseYTVideos from 'Helpers/Video/VideoHelpers';

/**
 * @param {Array.object} children - Array of children to use as slides.
 * @param {Object} sliderSettings - Settings to customise the slider. If none are provided, defualts below will be used.
 * @param {string} sliderClass - Class that can be added to the top level of the slider.
 * @param {boolean} sliderLightTheme - Boolean to force the light theme colours.
 * @param {boolean} sliderCentered - Boolean to center slides when there are fewer that slidesToShow. Default is left aligned.
 * @param {boolean} interacted - Boolean to control whether the slider has been interacted with. This is used to force all slides to load.
 * @param {boolean} fullWidth - Boolean to control whether the a slide displays at 100% width when there is 1 slideToShow. By default this will be 90% so that the next slide is partially visible.
 * @param {boolean} resetCarousel - Boolean to control whether the slider resets to slide 1 (sliderPosition 0) if the children change. This is true by default but can be overwritten.
 * @param {boolean} containsYouTubeVideos - Boolean to tell the slider there are YouTube videos in the slide. These can then be paused when the slide changes.
 * @param {function} afterClickFunction - Function that will be called when clicking an arrow or dot. Only works on LG and above. Returns the current active slide number.
*/

export default function SiteSlider({
  children,
  sliderSettings,
  sliderClass,
  sliderLightTheme,
  sliderCentered,
  interacted,
  fullWidth,
  resetCarousel,
  containsYouTubeVideos,
  afterClickFunction,
}) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [sliderInteracted, setSliderInteracted] = useState(interacted || false);

  const sliderRef = useRef(null);
  const slidesRef = useRef([]);

  const allSlides = React.Children.toArray(children);

  const {
    isBelowLg,
    isAboveMd,
    currentBreakpoint,
  } = useViewport();

  // These basic settings will be used if none are provided.
  const defaultSettings = {
    xs: {
      slidesToShow: 1,
      slidesToScroll: 1,
      sliderWidth: 375,
      dots: true,
      arrows: false,
      sliderGap: 16,
    },
    sm: {
      slidesToShow: 2,
      slidesToScroll: 2,
      sliderWidth: 524,
      dots: true,
      arrows: false,
      sliderGap: 16,
    },
    md: {
      slidesToShow: 2,
      slidesToScroll: 2,
      sliderWidth: 704,
      dots: true,
      arrows: false,
      sliderGap: 16,
    },
    lg: {
      slidesToShow: 3,
      slidesToScroll: 3,
      sliderWidth: 944,
      dots: true,
      arrows: true,
      sliderGap: 24,
    },
    xl: {
      slidesToShow: 4,
      slidesToScroll: 4,
      sliderWidth: 1124,
      dots: true,
      arrows: true,
      sliderGap: 24,
    },
    xxl: {
      slidesToShow: 4,
      slidesToScroll: 4,
      sliderWidth: 1324,
      dots: true,
      arrows: true,
      sliderGap: 32,
    },
  };

  // Use settings passed in, otherwise fallback to default settings
  const slidesToShow = get(sliderSettings[currentBreakpoint], 'slidesToShow', defaultSettings[currentBreakpoint].slidesToShow);
  const sliderWidth = get(sliderSettings[currentBreakpoint], 'sliderWidth', defaultSettings[currentBreakpoint].sliderWidth);
  const slidesToScroll = get(sliderSettings[currentBreakpoint], 'slidesToScroll', defaultSettings[currentBreakpoint].slidesToScroll);
  const sliderGap = get(sliderSettings[currentBreakpoint], 'sliderGap', defaultSettings[currentBreakpoint].sliderGap);

  let showDots = get(sliderSettings[currentBreakpoint], 'dots', defaultSettings[currentBreakpoint].dots);
  let showArrows = get(sliderSettings[currentBreakpoint], 'arrows', defaultSettings[currentBreakpoint].arrows);

  // If there are fewer slides than slidesToShow, dots and arrows are not needed
  if (allSlides.length <= slidesToShow) {
    showDots = false;
    showArrows = false;
  }

  // Remove dots on touch devices
  if (isBelowLg) {
    showDots = false;
  }

  // How many slides are not in a full set.
  // This is used to calculate how many slides to scroll.
  const slidesRemainder = allSlides.length % slidesToShow;

  const calculateSlideWidth = useCallback(() => {
    // On mobile slides default to 85% width. This is so that the next slide is partially visible
    let width = '85%';

    // If there is only 1 slide it needs to be 100% width
    if (allSlides.length === 1) width = '100%';

    // If slidesToShow is 1 on desktop, the slide will take up 90% of the sliderWidth
    if (slidesToShow === 1 && isAboveMd) {
      width = sliderWidth * 0.9;
    }

    // If slidesToShow is 1 on desktop and fullWidth is true, the slide will take up 100% of the sliderWidth
    if (slidesToShow === 1 && isAboveMd && fullWidth) {
      width = sliderWidth;
    }

    // If slidesToShow is more than 1 on mobile, slides share 85% of the available space so that the next slide is partially visible
    if (slidesToShow > 1 && !isAboveMd) {
      width = ((sliderWidth * 0.85) - (sliderGap * (slidesToShow - 1))) / slidesToShow;
    }

    // If slidesToShow is more than 1 on desktop, share the sliderWidth minus gaps with slidesToShow
    if (slidesToShow > 1 && isAboveMd) {
      width = (sliderWidth - (sliderGap * (slidesToShow - 1))) / slidesToShow;
    }
    return width;
  }, [sliderWidth, sliderGap, slidesToShow, isAboveMd, allSlides, fullWidth]);


  useEffect(() => {
    setSlideWidth(calculateSlideWidth());
  }, [calculateSlideWidth]);

  // Reset Carousel if children change
  useEffect(() => {
    if (children && resetCarousel) {
      setSliderPosition(0);
      setActiveSlide(0);
    }
  }, [children, resetCarousel]);


  const clickDot = (dotNumber) => {
    if (containsYouTubeVideos) {
      pauseYTVideos();
    }
    setSliderPosition(-((slideWidth * dotNumber) + (sliderGap * dotNumber)));
    setActiveSlide(dotNumber);
    setSliderInteracted(true);
    if (afterClickFunction) afterClickFunction(dotNumber);
  };

  const clickArrow = (direction) => {
    setSliderInteracted(true);

    if (containsYouTubeVideos) {
      pauseYTVideos();
    }

    if (direction === 'left') {
      if (slidesToScroll > 1) {
        // If were on a remainder slide, go back by the remainder otherwise go back slidesToShow
        if (activeSlide % slidesToScroll !== 0) {
          setSliderPosition(sliderPosition + ((slideWidth * slidesRemainder) + (sliderGap * slidesRemainder))); // eslint-disable-line max-len
          setActiveSlide(activeSlide - slidesRemainder);
          if (afterClickFunction) afterClickFunction(activeSlide - slidesRemainder);
        } else {
          setSliderPosition(sliderPosition + ((slideWidth * slidesToScroll) + (sliderGap * slidesToScroll))); // eslint-disable-line max-len
          setActiveSlide(activeSlide - slidesToScroll);
          if (afterClickFunction) afterClickFunction(activeSlide - slidesToScroll);
        }
      } else {
        setSliderPosition(sliderPosition + (slideWidth + sliderGap));
        setActiveSlide(activeSlide - 1);
        if (afterClickFunction) afterClickFunction(activeSlide - 1);
      }
    }

    if (direction === 'right') {
      if (slidesToScroll > 1) {
        // If clicking right would set activeSlide to more than the length of the children (allSlides) array, set the active slide to the last slide
        if ((activeSlide + slidesToScroll) >= allSlides.length - slidesRemainder) {
          setSliderPosition(sliderPosition - ((slideWidth * slidesRemainder) + (sliderGap * slidesRemainder))); // eslint-disable-line max-len
          setActiveSlide(activeSlide + slidesRemainder);
          if (afterClickFunction) afterClickFunction(activeSlide + slidesRemainder);
        } else {
          setSliderPosition(sliderPosition - ((slideWidth * slidesToScroll) + (sliderGap * slidesToScroll))); // eslint-disable-line max-len
          setActiveSlide(activeSlide + slidesToScroll);
          if (afterClickFunction) afterClickFunction(activeSlide + slidesToScroll);
        }
      } else {
        setSliderPosition(sliderPosition - (slideWidth + sliderGap));
        setActiveSlide(activeSlide + 1);
        if (afterClickFunction) afterClickFunction(activeSlide + 1);
      }
    }
  };

  // If use scrolls on a touch device, set interacted to true so that the slides render
  const handleScroll = () => {
    if (isBelowLg && !sliderInteracted) {
      setSliderInteracted(true);
    }
  };

  return (
    <>
      <section className={classNames(`SiteSlider ${sliderClass}`, { 'SiteSlider--single': allSlides.length === 1 }, { 'SiteSlider--dots': showDots }, { 'SiteSlider--lightTheme': sliderLightTheme })}>
        <div className="SiteSlider__slidesContainer">
          <div
            className={classNames('SiteSlider__slides', { 'SiteSlider__slides--centered': sliderCentered && allSlides.length <= slidesToShow })}
            ref={sliderRef}
            style={{ left: `${sliderPosition}px`, gap: `${sliderGap}px` }}
            onScroll={handleScroll}
          >
            {allSlides.map((child, index) => {
              // Only load elements in the DOM which are in view, or after the slider has been interacted with
              let loadSlide = (index < slidesToShow || sliderInteracted);
              if (isBelowLg && (index <= slidesToShow || sliderInteracted)) {
                loadSlide = true;
              }
              if (isAboveMd && slidesToShow === 1 && (index <= slidesToShow || sliderInteracted)) {
                loadSlide = true;
              }

              // Work out if slide is hidden outside of the view
              let isSlideHidden = index >= (activeSlide + slidesToShow) || index < activeSlide;
              if (isBelowLg || slidesToShow === 1) {
                isSlideHidden = index >= (activeSlide + slidesToShow + 1) || index < activeSlide;
              }

              return (
                <div
                  className={classNames('SiteSlider__slide', { 'SiteSlider__slide--hidden': isSlideHidden && !sliderInteracted })}
                  style={{ width: slideWidth }}
                  ref={(element) => { slidesRef.current[index] = element; }}
                  key={`slide_${child.key}`}
                  aria-hidden={isSlideHidden}
                  tabIndex={-1}
                >
                  {loadSlide && React.cloneElement(child)}
                </div>
              );
            })}
          </div>
        </div>

        {showArrows &&
          <>
            <div className="SiteSlider__arrow SiteSlider__arrow--left">
              <button aria-label="Previous" disabled={activeSlide === 0} onClick={() => { clickArrow('left'); }}>
                <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
              </button>
            </div>

            <div className="SiteSlider__arrow SiteSlider__arrow--right">
              <button aria-label="Next" disabled={activeSlide === allSlides.length - slidesToShow} onClick={() => { clickArrow('right'); }}>
                <FontAwesomeIcon icon={faChevronRight} title="Next" />
              </button>
            </div>
          </>
        }

        {showDots &&
          <div className="SiteSlider__dots">
            {allSlides.slice(0, allSlides.length - (slidesToShow - 1)).map((dot, index) => {
              // Show 1 dot per child, minus the last slide as this dot will not move the panel.
              // If we scroll more than 1 slide at a time. Hide the dots that are skipped.
              let hideDot = false;
              if (
                slidesToScroll > 1 &&
                (index % slidesToScroll !== 0) &&
                index + 1 !== allSlides.length - (slidesToShow - 1)
              ) {
                hideDot = true;
              }
              return (
                <button
                  className={classNames('SiteSlider__dots__dot', { 'SiteSlider__dots__dot--active': activeSlide === index }, { 'SiteSlider__dots__dot--hidden': hideDot })}
                  aria-label={`Go to slide ${index + 1} of ${allSlides.length - 1}`}
                  key={`dot_${dot.key}`}
                  tabIndex={0}
                  onClick={() => { clickDot(index); }}
                >
                  <div />
                </button>
              );
            },
            )}
          </div>
        }
      </section>
    </>
  );
}

SiteSlider.propTypes = {
  sliderSettings: PropTypes.shape({}),
  sliderClass: PropTypes.string,
  sliderLightTheme: PropTypes.bool,
  sliderCentered: PropTypes.bool,
  interacted: PropTypes.bool,
  fullWidth: PropTypes.bool,
  resetCarousel: PropTypes.bool,
  containsYouTubeVideos: PropTypes.bool,
  afterClickFunction: PropTypes.func,
};

SiteSlider.defaultProps = {
  sliderSettings: {},
  sliderClass: '',
  sliderLightTheme: false,
  sliderCentered: false,
  interacted: false,
  fullWidth: false,
  resetCarousel: true,
  containsYouTubeVideos: false,
  afterClickFunction: () => {},
};
